import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Row, Col } from 'antd'
import { format } from 'date-fns'
import moment from 'moment'

import { Button, InputField } from '../../components'
import { PLACEHOLDER_MESSAGES } from '../../utils/constants/messages'
import AutoFieldComponent from '../AutoComplete/AutoComplete'
import SelectField from '../SelectField/SelectField'
import DateField from '../DateField/DateField'
import {
  DATE_FORMATS,
  INVOICE_STATUS,
  financialTypes,
} from '../../utils/constants/constant'
import { staticOptionsSelector } from '../../redux/reducer/getOptionsSlice'
import {
  invoiceDateRule,
  invoiceDueDateRule,
  invoiceStatusRule,
} from '../../utils/constants/validationRules'
import {
  setTimeSheetsData,
  timeSheetsDataSelector,
} from '../../redux/reducer/timeSheetsDataSlice'
import DrawerModule from '../Drawer/Drawer'

const InoviceDetailsHeader = ({
  timesheetNameFormat,
  isCurrentInvoiceAddedInQB,
  open,
  onClose,
}) => {
  const [form] = Form.useForm()
  const { financialTypeOptions } = useSelector(staticOptionsSelector)
  const { timeSheetsData } = useSelector(timeSheetsDataSelector)

  const currentInvoiceTimesheetData =
    timeSheetsData && timeSheetsData[timesheetNameFormat]

  const dispatch = useDispatch()
  const currentDate = new Date()

  useEffect(() => {
    form.setFieldsValue({
      projectName: currentInvoiceTimesheetData?.projectName,
      projectModel: currentInvoiceTimesheetData?.projectModel,
      payModel: currentInvoiceTimesheetData?.payModel,
      startDate: moment(
        currentInvoiceTimesheetData?.invoiceGenerateDetails?.startDate,
        DATE_FORMATS.DD_MMM_YYYY
      ),
      endDate: moment(
        currentInvoiceTimesheetData?.invoiceGenerateDetails?.endDate,
        DATE_FORMATS.DD_MMM_YYYY
      ),
      invoiceDueDate: moment(
        currentInvoiceTimesheetData?.invoiceGenerateDetails?.invoiceDueDate,
        DATE_FORMATS.DD_MMM_YYYY
      ),
      currency: currentInvoiceTimesheetData?.currency,
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentInvoiceTimesheetData])

  /**
   *  This function updates invoice details in cached timesheet data
   */
  const editInvoiceDetails = useCallback(
    (updatedKey, newValue) => {
      const updatedInvoiceDetails = {
        ...currentInvoiceTimesheetData?.invoiceGenerateDetails,
        [updatedKey]: newValue,
      }
      const newTimesheetData = {
        ...timeSheetsData,
        [timesheetNameFormat]: {
          ...currentInvoiceTimesheetData,
          invoiceGenerateDetails: updatedInvoiceDetails,
        },
      }
      dispatch(setTimeSheetsData(newTimesheetData))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timeSheetsData, currentInvoiceTimesheetData]
  )

  return (
    <DrawerModule
      title={'Invoice Details'}
      open={open}
      onClose={onClose}
      size={600}
    >
      <Form form={form} className="invoice-details-form">
        <Row gutter={16}>
          <Col sm={4} md={4} lg={12}>
            <AutoFieldComponent
              label="Project Name"
              placeholder={PLACEHOLDER_MESSAGES.projectNameMessage}
              name="projectName"
              type="text"
              disabled
              existingValue={currentInvoiceTimesheetData?.projectName}
            />
          </Col>
          <Col sm={4} md={4} lg={12}>
            <InputField
              label="Project Model"
              placeholder={PLACEHOLDER_MESSAGES.projectModelMessage}
              type="text"
              name="projectModel"
              disabled
              existingValue={currentInvoiceTimesheetData?.projectModel}
            />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col sm={4} md={4} lg={12}>
            <InputField
              name="payModel"
              label="Pay Model"
              required
              disabled
              existingValue={currentInvoiceTimesheetData?.payModel}
              placeholder={PLACEHOLDER_MESSAGES?.payModelMessage}
              type="text"
            />
          </Col>
          <Col sm={4} md={4} lg={12}>
            <SelectField
              name="financialType"
              label="Financial Type"
              placeholder={PLACEHOLDER_MESSAGES.financialTypeMessage}
              options={financialTypeOptions}
              type="text"
              existingValue={
                currentInvoiceTimesheetData?.invoiceGenerateDetails
                  ?.financialType || financialTypes?.REVENUE
              }
              disabled={isCurrentInvoiceAddedInQB}
              onChange={(value) => {
                editInvoiceDetails('financialType', value)
              }}
            />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col sm={4} md={4} lg={12}>
            <SelectField
              name="invoiceStatus"
              label="Invoice Status"
              placeholder={PLACEHOLDER_MESSAGES.invoiceStatusMessage}
              type="text"
              options={['Deposit Settlement', 'Pending']}
              existingValue={
                currentInvoiceTimesheetData?.invoiceGenerateDetails
                  ?.invoiceStatus || INVOICE_STATUS?.PENDING
              }
              required
              disabled={isCurrentInvoiceAddedInQB}
              rules={invoiceStatusRule}
              onChange={(value) => {
                editInvoiceDetails('invoiceStatus', value)
              }}
            />
          </Col>
          <Col sm={4} md={4} lg={12}>
            <DateField
              name="invoiceDate"
              label="Invoice Date"
              placeholder={PLACEHOLDER_MESSAGES.invoiceDateMessage}
              existingValue={
                currentInvoiceTimesheetData?.invoiceGenerateDetails
                  ?.invoiceDate || format(currentDate, DATE_FORMATS.dd_MMM_yyyy)
              }
              type="text"
              required
              disabled={isCurrentInvoiceAddedInQB}
              rules={invoiceDateRule}
              onDateChange={(_, dateString) => {
                editInvoiceDetails('invoiceDate', dateString)
              }}
            />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col sm={4} md={4} lg={12}>
            <DateField
              name="invoiceDueDate"
              label="Due Date"
              placeholder={PLACEHOLDER_MESSAGES.invoiceDueDateMessage}
              existingValue={
                currentInvoiceTimesheetData?.invoiceGenerateDetails
                  ?.invoiceDueDate
              }
              type="text"
              required
              rules={invoiceDueDateRule}
              onDateChange={(_, dateString) => {
                editInvoiceDetails('invoiceDueDate', dateString)
              }}
            />
          </Col>
          <Col sm={4} md={4} lg={12}>
            <DateField
              name="startDate"
              label="Start Date"
              placeholder={PLACEHOLDER_MESSAGES.startDateMessage}
              type="text"
              existingValue={
                currentInvoiceTimesheetData?.invoiceGenerateDetails?.startDate
              }
              required
              onDateChange={(_, dateString) => {
                editInvoiceDetails('startDate', dateString)
              }}
            />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col sm={4} md={4} lg={12}>
            <DateField
              name="endDate"
              label="End Date"
              placeholder={PLACEHOLDER_MESSAGES.endDateMessage}
              type="text"
              existingValue={
                currentInvoiceTimesheetData?.invoiceGenerateDetails?.endDate
              }
              onDateChange={(_, dateString) => {
                editInvoiceDetails('endDate', dateString)
              }}
              required
            />
          </Col>
          {currentInvoiceTimesheetData?.invoiceGenerateDetails?.taxDetails
            ?.taxable && (
            <>
              {currentInvoiceTimesheetData?.provider !== 'ZOHO_BOOKS' && (
                <Col md={4} lg={12}>
                  <SelectField
                    name="globalTaxSetting"
                    label="Amount are: "
                    placeholder={PLACEHOLDER_MESSAGES.taxApplicabilityMessage}
                    disabled
                    existingValue={
                      currentInvoiceTimesheetData?.invoiceGenerateDetails
                        ?.taxDetails?.globalTaxSetting
                    }
                  />
                </Col>
              )}
              {currentInvoiceTimesheetData?.invoiceGenerateDetails?.taxDetails
                ?.globalTaxSetting !== 'Not Applicable' && (
                <Col md={4} lg={12}>
                  <SelectField
                    name="taxCodeId"
                    label="Tax Rate"
                    showSearch={true}
                    disabled
                    existingValue={
                      currentInvoiceTimesheetData?.invoiceGenerateDetails
                        ?.taxDetails?.taxName
                    }
                  />
                </Col>
              )}
            </>
          )}
        </Row>
        <Row gutter={16}>
          <Col md={4} lg={12}>
            <InputField
              name="currency"
              label="Currency"
              disabled
              placeholder={PLACEHOLDER_MESSAGES.currencyMessage}
              existingValue={currentInvoiceTimesheetData?.currency}
            />
          </Col>
        </Row>
        <div className="invoice-details-footer">
          <Button label="Cancel" danger />
          <Button label="Save" type="primary" />
        </div>
      </Form>
    </DrawerModule>
  )
}

export default InoviceDetailsHeader
